import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtNumber } from "@/lib/api";
import { useCapital } from "@/context/CapitalContext";
import { TID } from "@/constants/testIds";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { AIPanel } from "@/components/capital/AskAssetAI";

export function SiteAnalysis() {
  const { client } = useCapital();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!client) return;
    setLoading(true);
    api.get("/capital/site-analysis", { params: { client } }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [client]);

  const top15 = data.slice(0, 15);

  return (
    <>
    <AIPanel screen="sites" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-display text-lg font-semibold text-slate-900">Top sites by renewal spend</h3>
          <p className="text-xs text-slate-500 mb-4">Immediate + 12M + 24M combined</p>
          <div className="h-96">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={top15} layout="vertical" margin={{ top: 5, right: 20, left: 5, bottom: 0 }}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => `$${Math.round(v/1000)}k`} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis type="category" dataKey="site" tick={{ fill: "#334155", fontSize: 11 }} axisLine={false} tickLine={false} width={180} />
                <Tooltip formatter={(v) => `$${Number(v).toLocaleString()}`} contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: "#e2e8f0" }} />
                <Bar dataKey="total_renewal_budget" fill="#00334C" radius={[0, 3, 3, 0]} maxBarSize={16} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-5 pt-5 pb-3">
            <h3 className="font-display text-lg font-semibold text-slate-900">Site Analysis</h3>
            <p className="text-xs text-slate-500">Renewal spend across all sites</p>
          </div>
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 z-10">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="text-left font-medium px-4 py-2.5">Site</th>
                  <th className="text-right font-medium px-4 py-2.5">Assets</th>
                  <th className="text-right font-medium px-4 py-2.5">Imm+12M</th>
                  <th className="text-right font-medium px-4 py-2.5">24M</th>
                  <th className="text-right font-medium px-4 py-2.5">Total</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="p-4"><div className="h-6 rounded shimmer" /></td></tr>
                ) : (
                  data.map((s) => (
                    <tr key={s.site} data-testid={TID.capitalSiteRow(s.site)} className="border-b border-slate-100 hover:bg-slate-50/70">
                      <td className="px-4 py-2.5 text-slate-800 max-w-[220px] truncate">{s.site}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{fmtNumber(s.asset_count)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{fmtCurrency(s.immediate_12m_budget)}</td>
                      <td className="px-4 py-2.5 text-right text-slate-600 tabular-nums">{fmtCurrency(s.twenty_four_m_budget)}</td>
                      <td className="px-4 py-2.5 text-right font-medium text-slate-900 tabular-nums">{fmtCurrency(s.total_renewal_budget)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}
